import { Add, Headphones, PlayFilledAlt } from '@carbon/icons-react';
import { AdaptiveWidth, Pad, PadBox, Separator } from 'component/basics';
import { SubtleButton } from 'component/button';
import { colorSurfaceHighlightShade2, colorSurfacePrimary, colorTextPrimary } from 'component/color';
import { fromNow } from 'component/date';
import { UtilityText, WebLinkTextButton } from 'component/text';
import { useLanguage } from 'component/translation';
import { useCallback, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import type { TopicConfig } from 'structure/topic';
import { useCollection } from 'util/datastore';
import type { Feature } from 'util/stdtypes';
import { declareTable, t } from 'util/stdtypes';
import { keyToUrl } from 'util/util';

export const ARTICLE_ICON_TYPES = {
    audio: 'audio',
    media: 'media',
    text: 'text',
};

export const ReadingListFeature: Feature<TopicConfig, {}> = {
    key: 'readinglist',
    name: 'Reading List',
    providesConfig: {
        topicBottomWidgets: [ReadingListWidget],
    },
};

export type BacklinkArticle = {
    key: string;
    title: string;
    url: string;
    image: string;
    type: string;
};
const backlinkArticleTable = declareTable<BacklinkArticle>('backlink_article');

export function ReadingListWidget() {
    const articles = useCollection(backlinkArticleTable);

    return <ReadingList articles={articles} />;
}

export function ReadingList({ articles }) {
    const [visibleCount, setVisibleCount] = useState(3);
    const showMore = useCallback(() => {
        setVisibleCount((prevCount) => prevCount + 3);
    }, []);

    if (!articles || articles.length === 0) {
        return null;
    }
    const visibleArticles = articles.slice(0, visibleCount);

    return (
        <PadBox horiz={0} top={32}>
            <DiscoveryBox>
                <PadBox horiz={20} vert={32}>
                    <UtilityText color={colorTextPrimary} label={t('Learn more about this topic')} />
                    <Pad size={12} />
                    {visibleArticles.map((article, i) => (
                        <ArticlePreview article={article} first={i === 0} key={article.key} />
                    ))}
                    {visibleCount < articles.length && (
                        <>
                            <Pad size={32} />
                            <SubtleButton color={colorTextPrimary} icon={Add} label={t('Show more')} onPress={showMore} padRight />
                        </>
                    )}
                </PadBox>
            </DiscoveryBox>
        </PadBox>
    );
}

function DiscoveryBox({ children }) {
    const s = DiscoveryBoxStyle;
    return <View style={s.box}>{children}</View>;
}
const DiscoveryBoxStyle = {
    box: {
        backgroundColor: colorSurfaceHighlightShade2,
    },
};

function getDateString(date: Date, language: string) {
    try {
        const d = new Date(date);
        return fromNow(d.getTime(), language);
    } catch (e) {
        return '';
    }
}

function ArticleIcon({ type }) {
    switch (type) {
        case ARTICLE_ICON_TYPES.audio:
            return <Headphones height={16} width={16} />;
        case ARTICLE_ICON_TYPES.media:
            return <PlayFilledAlt height={16} width={16} />;
        default:
            return null;
    }
}

function ArticlePreview({ first, article }) {
    const s = ArticlePreviewStyle;
    const language = useLanguage();
    // TEMPORARY: use the article url as a fallback for relatedArticle that were created with stringToKey
    const url = article.url || keyToUrl(article.key);
    const dateString = getDateString(article.date, language);
    const [narrow, setNarrow] = useState(false);

    return (
        <AdaptiveWidth breakpoint={400} setNarrow={setNarrow}>
            {!first && (
                <PadBox top={20}>
                    <Separator />
                </PadBox>
            )}
            <View style={s.outer}>
                <View style={s.imageWrapper}>
                    {article.type && article.type !== ARTICLE_ICON_TYPES.text && (
                        <View style={s.iconWrapper}>
                            <ArticleIcon type={article.type} />
                        </View>
                    )}
                    <Image
                        onError={(e) => console.warn('Failed to load article image:', article.key)}
                        source={{ uri: article.image }}
                        style={[s.image, narrow ? { width: 70 } : {}]}
                        // TODO: add a NP placeholder image
                        //defaultSource={}
                    />
                </View>
                <View style={s.right}>
                    <WebLinkTextButton
                        color={colorTextPrimary}
                        heading
                        linkType='related-article-click'
                        text={article.title}
                        type='small'
                        url={url}
                        weight='strong'
                    />
                    <Pad size={8} />
                    <UtilityText color={colorTextPrimary} text={dateString} />
                </View>
            </View>
        </AdaptiveWidth>
    );
}

const ArticlePreviewStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        marginTop: 20,
    },
    imageWrapper: {
        overflow: 'hidden',
        position: 'relative',
        borderRadius: 8,
    },
    image: {
        width: 100,
        height: 70,
        borderRadius: 8,
    },
    right: {
        marginLeft: 20,
        flexShrink: 1,
    },
    iconWrapper: {
        zIndex: 2,
        position: 'absolute',
        bottom: 8,
        left: 8,
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        borderRadius: 1000,
        backgroundColor: colorSurfacePrimary,
    },
});
